import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { Organization } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { PlusIcon, UsersGroupIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/organizations.html + its view
 * admin/routes.py:organizations() (~1253-1274).
 *
 * Only `region_id` and `status` are real server-side filters on
 * GET /api/v1/admin/orgs (api/routes.py api_admin_orgs_list(), which calls
 * org_service.list_orgs(region_id, status, limit)) - `search` and
 * `district_id` have no server support, so both are filtered client-side
 * here, same as the admin/users.html port (admin/Users.tsx) does for its
 * own district filter, and matching what the Jinja view itself did (in
 * Python, after fetching) rather than any real RTDB-level filter.
 *
 * status="" (the "All" option below) relies on a concurrent backend fix to
 * api_admin_orgs_list(): today it does `status = request.args.get("status")
 * or "active"`, which silently coerces an explicit empty string back to
 * "active" - the exact bug already fixed once for /admin/users. Sent as
 * specified regardless of that; once fixed server-side this "just works". */

interface AdminOrgsResponse { ok: boolean; items: Organization[] }

function isString(v: string | undefined): v is string { return Boolean(v) }

export default function AdminOrganizations() {
  const [search, setSearch] = useState('')
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [status, setStatus] = useState('')

  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs', regionId, status],
    queryFn: () => api.get<AdminOrgsResponse>(
      `/admin/orgs?region_id=${encodeURIComponent(regionId)}&status=${encodeURIComponent(status)}&limit=200`,
    ),
  })

  // No regions/districts catalog endpoint exists anywhere in the JSON API
  // (see admin/Users.tsx for the identical gap) - harvest distinct
  // region_id/district_id values actually present on orgs, from one
  // unfiltered, long-cached fetch so the dropdowns don't shrink to whatever
  // the active region/status filters happen to return.
  const metaQuery = useQuery({
    queryKey: ['admin', 'orgs-meta'],
    queryFn: () => api.get<AdminOrgsResponse>('/admin/orgs?status=&limit=1000'),
    staleTime: 5 * 60 * 1000,
  })
  const metaItems = metaQuery.data?.items ?? []
  const regionOptions = Array.from(new Set(metaItems.map((o) => o.region_id).filter(isString))).sort()
  const districtOptions = Array.from(new Set(metaItems.map((o) => o.district_id).filter(isString))).sort()

  const rawOrgs = orgsQuery.data?.items ?? []
  let orgs = districtId ? rawOrgs.filter((o) => o.district_id === districtId) : rawOrgs
  if (search) {
    const q = search.toLowerCase()
    orgs = orgs.filter((o) => (o.name ?? '').toLowerCase().includes(q))
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Organisations</h1>
        </div>
      </div>

      <Card style={{ marginBottom: 'var(--sp-4)' }}>
        <CardBody>
          <div className="filter-scroll">
            <Input label="Search" placeholder="Organisation name…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Select label="Region" value={regionId} onChange={(e) => setRegionId(e.target.value)}>
              <option value="">All Regions</option>
              {regionOptions.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
            </Select>
            <Select label="District" value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
              <option value="">All Districts</option>
              {districtOptions.map((d) => <option key={d} value={d}>{titleCase(d)}</option>)}
            </Select>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </Select>
          </div>
        </CardBody>
      </Card>

      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <p className="page-head__sub" style={{ margin: 0 }}>
          {orgs.length} organisation{orgs.length !== 1 ? 's' : ''} found
        </p>
        <Link to="/organizations/new" className="fab-trigger__inline"><Button size="sm">+ New Organisation</Button></Link>
      </div>
      {/* PWA: creation is the page's primary action, so it lives on the
          thumb-reach FAB; the inline button above stays desktop-only. */}
      <Link to="/organizations/new" className="fab" aria-label="New organisation">
        <PlusIcon size={22} />
      </Link>

      {orgsQuery.isError ? (
        <Alert tone="danger">{(orgsQuery.error as Error).message}</Alert>
      ) : orgsQuery.isLoading ? (
        <div className="stack"><Skeleton h={90} /><Skeleton h={90} /></div>
      ) : orgs.length === 0 ? (
        <EmptyState icon={<UsersGroupIcon size={22} />} title="No organisations found" body="" />
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {orgs.map((o) => <OrgCard key={o.id} org={o} />)}
        </div>
      )}
    </>
  )
}

/* The whole card is a link to the org's detail page - unlike admin/Users.tsx's
 * Companies-tab OrgCard, which (until this phase) could only show a small
 * "View" link since /organizations/:orgId didn't exist yet. Matches the
 * Jinja original, where every org card is an <a> wrapping the full tile. */
function OrgCard({ org }: { org: Organization }) {
  return (
    <Link to={`/organizations/${org.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card pad hover>
        <div className="row" style={{ justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name || '—'}</strong>
          <Badge tone={org.status === 'active' ? 'success' : 'danger'}>{titleCase(org.status)}</Badge>
        </div>
        <p className="page-head__sub" style={{ margin: '2px 0 10px' }}>{titleCase(org.type)}</p>
        <div className="row" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {org.region_id && <span>{titleCase(org.region_id)}{org.district_id ? `, ${titleCase(org.district_id)}` : ''}</span>}
          <span>{org.member_count ?? 0} member{(org.member_count ?? 0) !== 1 ? 's' : ''}</span>
        </div>
      </Card>
    </Link>
  )
}
